import { useQueryClient } from '@tanstack/react-query';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { useTrackCanvasMediaExported } from '@air/analytics';
import {
  CANVAS_NODE_EXPORT_DROP_TYPE,
  type CanvasNodeExportPayload,
  useEditCanvasStore,
} from '@air/feature-edit-canvas';
import { Uploads } from '@air/next-icons';
import { useAirModal } from '@air/provider-modal';
import { cn } from '@air/utils-tailwind';

import { PreUploadModal } from '~/components/Modals/PreUploadModal';
import { importCanvasNodesToBoard, resolveExportPayloadsMedia } from '~/hooks/canvases/canvasImportUtils';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useRefreshImportTasks } from '~/swr-hooks/importer/useRefreshImportTasks';
import { useAddAssetsToAllViews } from '~/utils/mutateUtils/GalleryAssets';
import { useCreateTask } from '~/utils/taskUtils/useCreateTask';

export type BoardImportDropTargetProps = {
  boardId?: string;
};

type CanvasDragEvent = CustomEvent<{
  clientX?: number;
  clientY?: number;
  payloads?: CanvasNodeExportPayload[];
  phase: 'drop' | 'move' | 'start';
}>;

const isCanvasDragEvent = (e: Event): e is CanvasDragEvent =>
  e instanceof CustomEvent && typeof e.detail?.phase === 'string';

/**
 * Drop target for importing canvas nodes back to the workspace.
 * Listens for custom events dispatched by DragToWorkspaceOverlay (CMD/CTRL+drag
 * on canvas image nodes).
 *
 * On board pages (boardId provided): imports directly to that board.
 * On non-board pages: shows the PreUploadModal board-picker first.
 */
export const BoardImportDropTarget = memo(({ boardId }: BoardImportDropTargetProps) => {
  const dropTargetRef = useRef<HTMLDivElement>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const { currentWorkspace } = useCurrentWorkspace();
  const { createTask } = useCreateTask();
  const { refreshImports } = useRefreshImportTasks();
  const { addAssetsToAllViews } = useAddAssetsToAllViews();
  const queryClient = useQueryClient();
  const [showPreUploadModal] = useAirModal(PreUploadModal);
  const { trackCanvasMediaExported } = useTrackCanvasMediaExported();

  const isOverDropTarget = useCallback((clientX: number, clientY: number) => {
    const el = dropTargetRef.current;

    if (!el) {
      return false;
    }
    const rect = el.getBoundingClientRect();

    return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
  }, []);

  const handleDrop = useCallback(
    async (payloads: CanvasNodeExportPayload[]) => {
      setIsDraggingOver(false);

      if (!payloads.length) {
        return;
      }

      const workspaceId = currentWorkspace?.id;

      if (!workspaceId) {
        return;
      }

      const resolved = await resolveExportPayloadsMedia(payloads);

      if (!resolved.length) {
        return;
      }

      const runImport = async (targetBoardId?: string) => {
        await importCanvasNodesToBoard({
          addAssetsToAllViews,
          boardId: targetBoardId,
          createTask,
          queryClient,
          refreshImports,
          resolved,
          workspaceId,
          workspaceImage: currentWorkspace?.image ?? '',
          workspaceName: currentWorkspace?.name ?? '',
        });
      };

      const trackExports = (targetBoardId?: string) => {
        const canvasId = useEditCanvasStore.getState().canvasId;

        if (!canvasId) {
          return;
        }
        trackCanvasMediaExported({
          canvas_id: canvasId,
          number_of_media: resolved.length,
          destination: 'workspace',
          ...(targetBoardId && { board_id: targetBoardId }),
        });
      };

      if (boardId) {
        await runImport(boardId);
        trackExports(boardId);
      } else {
        showPreUploadModal({
          onCancel: () => {},
          onUpload: async (selectedBoard) => {
            await runImport(selectedBoard?.id);
            trackExports(selectedBoard?.id);
          },
        });
      }
    },
    [
      addAssetsToAllViews,
      boardId,
      createTask,
      currentWorkspace,
      queryClient,
      refreshImports,
      showPreUploadModal,
      trackCanvasMediaExported,
    ],
  );

  useEffect(() => {
    const onCanvasDrag = (e: Event) => {
      if (!isCanvasDragEvent(e)) {
        return;
      }
      const { detail } = e;

      if (detail.phase === 'start') {
        setIsDraggingOver(false);
        return;
      }

      if (detail.phase === 'move' && detail.clientX !== undefined && detail.clientY !== undefined) {
        setIsDraggingOver(isOverDropTarget(detail.clientX, detail.clientY));
        return;
      }

      if (detail.phase === 'drop') {
        const over =
          detail.clientX !== undefined &&
          detail.clientY !== undefined &&
          isOverDropTarget(detail.clientX, detail.clientY);
        setIsDraggingOver(false);
        if (over && detail.payloads) {
          handleDrop(detail.payloads);
        }
      }
    };

    document.addEventListener(CANVAS_NODE_EXPORT_DROP_TYPE, onCanvasDrag);
    return () => {
      document.removeEventListener(CANVAS_NODE_EXPORT_DROP_TYPE, onCanvasDrag);
    };
  }, [handleDrop, isOverDropTarget]);

  return (
    <div
      ref={dropTargetRef}
      className={cn(
        'absolute top-0 right-2 bottom-10 left-2 z-50',
        isDraggingOver ? 'pointer-events-auto' : 'pointer-events-none',
      )}
    >
      {isDraggingOver && (
        <div
          data-testid="BOARD_IMPORT_DROP_TARGET"
          aria-hidden
          className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-12 border-2 border-blue-9 border-dashed bg-blue-3/50"
        >
          <Uploads className="size-6" />
          <div className="flex flex-col items-center gap-1 text-center">
            <h2 className="font-medium text-16 text-grey-12">Drop files here to import</h2>
          </div>
        </div>
      )}
    </div>
  );
});

BoardImportDropTarget.displayName = 'BoardImportDropTarget';
